import { useState } from 'preact/hooks';
import Decimal from 'decimal.js';
import { simulator } from '../engine/tfsa';
import { formatCAD } from '../lib/decimal';
import { clampNonNegative } from '../lib/clamp';
import type { TFSAState } from '../engine/types';

interface SimulatorProps {
  tfsa: TFSAState;
}

export function Simulator({ tfsa }: SimulatorProps) {
  const [expanded, setExpanded] = useState(false);
  const [amount, setAmount] = useState('');

  const amountCents = new Decimal(amount || '0').times(100).toFixed(0);
  const result = expanded && amount ? simulator(tfsa, amountCents) : null;

  return (
    <section class="border border-card-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        class="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-cream focus:outline-none focus:ring-2 focus:ring-inset focus:ring-hudson-navy text-sm font-medium text-hudson-navy"
      >
        <span>What if I contribute…?</span>
        <span aria-hidden="true">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div class="px-4 pb-4 space-y-3 bg-white border-t border-card-border">
          <label class="block mt-3">
            <span class="text-sm font-medium text-body">Amount</span>
            <div class="relative mt-1">
              <span aria-hidden="true" class="absolute left-3 top-1/2 -translate-y-1/2 text-body/70">$</span>
              <input
                type="number"
                min={0}
                step={100}
                value={amount}
                aria-label="Hypothetical contribution amount"
                onInput={(e) =>
                  setAmount(clampNonNegative((e.target as HTMLInputElement).value))
                }
                class="block w-full pl-8 pr-3 py-2 border border-card-border rounded-lg focus:outline-none focus:ring-2 focus:ring-hudson-navy"
                placeholder="0"
              />
            </div>
          </label>

          {result && (
            <div class="space-y-2">
              <div class="flex items-center justify-between text-sm">
                <span class="text-body/70">Room after contribution</span>
                <span
                  class={`font-bold ${
                    result.over_contrib.over ? 'text-alert-red' : 'text-success-green'
                  }`}
                >
                  {formatCAD(result.projected_available_cents)}
                </span>
              </div>
              {result.over_contrib.over && (
                <div
                  role="alert"
                  class={`flex items-start gap-2 text-sm rounded px-3 py-2 border ${
                    result.over_contrib.severity === 'error'
                      ? 'bg-white border-alert-red text-alert-red'
                      : 'bg-cream border-grain-gold text-body/70'
                  }`}
                >
                  <span aria-hidden="true" class="font-bold">⚠</span>
                  <span>
                    {result.over_contrib.severity === 'error'
                      ? `This would put you over your limit.`
                      : `You'd be very close to your limit.`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
